import { useState, useEffect } from "react";
import { useOrganization } from "../../App";
import { creditsService, ProviderBalance } from "@/services/creditsService";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, RefreshCw, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import * as Sentry from "@sentry/react";

export const CreditsBalanceDashboard = () => {
  const { currentOrganization } = useOrganization();
  const [balances, setBalances] = useState<ProviderBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchBalances = async (isRefresh = false) => {
    if (!currentOrganization?.id) {
      setError("No organization found");
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await creditsService.getCreditsBalance(
        currentOrganization.id,
      );

      setBalances(response.balances);
      setLastFetched(response.fetched_at);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch credits balance";
      setError(errorMessage);
      Sentry.logger.error("Error in CreditsBalanceDashboard", {
        error: err,
        log_source: "credits_balance_dashboard",
        organization_id: currentOrganization?.id,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBalances();
  }, [currentOrganization?.id]);

  const handleRefresh = () => {
    fetchBalances(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "error":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "not_available":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusClasses = (status: string) => {
    switch (status) {
      case "success":
        return "border-green-200 bg-green-50 text-green-950 dark:border-green-800 dark:bg-green-950/30 dark:text-green-50";
      case "error":
        return "border-red-200 bg-red-50 text-red-950 dark:border-red-800 dark:bg-red-950/30 dark:text-red-50";
      case "not_available":
        return "border-yellow-200 bg-yellow-50 text-yellow-950 dark:border-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-50";
      default:
        return "";
    }
  };

  const getStatusDescriptionClasses = (status: string) => {
    switch (status) {
      case "success":
        return "text-green-700 dark:text-green-200";
      case "error":
        return "text-red-700 dark:text-red-200";
      case "not_available":
        return "text-yellow-700 dark:text-yellow-200";
      default:
        return "";
    }
  };

  const formatBalance = (balance?: number, currency?: string) => {
    if (balance === undefined || balance === null) return "N/A";
    const currencySymbol =
      currency?.toLowerCase() === "usd" ? "$" : currency || "$";
    return `${currencySymbol}${balance.toFixed(2)}`;
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Credits Balance</h1>
          <p className="text-muted-foreground mt-2">
            View provider-reported balances where supported
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          size="sm"
        >
          {refreshing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {lastFetched && (
        <p className="text-sm text-muted-foreground">
          Last updated: {formatDateTime(lastFetched)}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {balances.map((providerBalance) => (
          <Card
            key={providerBalance.provider}
            className={`transition-all ${getStatusClasses(providerBalance.status)}`}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">
                  {providerBalance.provider}
                </CardTitle>
                {getStatusIcon(providerBalance.status)}
              </div>
              <CardDescription
                className={getStatusDescriptionClasses(providerBalance.status)}
              >
                {providerBalance.status === "success" &&
                  "Balance fetched successfully"}
                {providerBalance.status === "error" && "Failed to fetch balance"}
                {providerBalance.status === "not_available" &&
                  "Balance API not available"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {providerBalance.status === "success" ? (
                <div className="space-y-2">
                  <div className="text-3xl font-bold">
                    {formatBalance(
                      providerBalance.balance,
                      providerBalance.currency,
                    )}
                  </div>
                  {providerBalance.details && (
                    <div className="mt-4 space-y-1">
                      {providerBalance.provider === "OpenAI" &&
                        providerBalance.details.total_granted !== undefined && (
                          <>
                            <p className="text-sm text-muted-foreground">
                              Total Granted:{" "}
                              {formatBalance(
                                providerBalance.details.total_granted,
                                providerBalance.currency,
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Total Used:{" "}
                              {formatBalance(
                                providerBalance.details.total_used,
                                providerBalance.currency,
                              )}
                            </p>
                          </>
                        )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Alert
                    variant={
                      providerBalance.status === "error"
                        ? "destructive"
                        : "default"
                    }
                  >
                    <AlertDescription className="text-sm">
                      {providerBalance.error_message ||
                        "Unknown error occurred"}
                    </AlertDescription>
                  </Alert>
                  {providerBalance.provider === "Cartesia" &&
                    providerBalance.status === "not_available" && (
                      <a
                        href="https://play.cartesia.ai/subscription"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline inline-block mt-2"
                      >
                        Visit Cartesia Dashboard →
                      </a>
                    )}
                  {providerBalance.provider === "OpenAI" &&
                    providerBalance.status === "not_available" && (
                      <a
                        href="https://platform.openai.com/settings/organization/billing/overview"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline inline-block mt-2"
                      >
                        Visit OpenAI Billing →
                      </a>
                    )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2">About Credits Balance</h3>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li>
            <strong>OpenAI:</strong> Credit balance is only available in the
            OpenAI dashboard. Secret API keys can be used for spend reporting,
            not credit balance.
          </li>
          <li>
            <strong>Deepgram:</strong> Displays the balance for the first
            project returned by your Deepgram API key. Balance checks require
            billing read access.
          </li>
          <li>
            <strong>Cartesia:</strong> No public balance API is available. Check
            the Cartesia dashboard directly.
          </li>
        </ul>
      </div>
    </div>
  );
};
